// client/src/services/userService.ts

import { apiClient, PaginatedResponse } from './api';
import { useAuthStore } from '../stores/authStore';

// Types pour les utilisateurs
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'super_admin' | 'admin' | 'driver';
  tenant_id?: string;
  tenant_name?: string;
  is_active: boolean;
  last_login_at?: string;
  email_verified: boolean;
  phone?: string;
  avatar?: string;
  preferences?: UserPreferences;
  permissions?: Record<string, boolean>;
  created_at: string;
  updated_at: string;
}

export interface CreateUserData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  role: 'super_admin' | 'admin' | 'driver';
  tenant_id?: string;
  phone?: string;
  permissions?: Record<string, boolean>;
}

export interface UpdateUserData {
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: 'super_admin' | 'admin' | 'driver';
  tenant_id?: string;
  phone?: string;
  is_active?: boolean;
  permissions?: Record<string, boolean>;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: 'super_admin' | 'admin' | 'driver';
  is_active?: boolean;
  tenant_id?: string;
}

export interface UserStats {
  total_users: number;
  super_admins: number;
  admins: number;
  drivers: number;
  active_users: number;
  inactive_users: number;
}

export interface PasswordChangeData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface UserProfileData {
  first_name?: string;
  last_name?: string;
  phone?: string;
}

export interface UserPreferences {
  language?: string;
  timezone?: string;
  theme?: string;
  dashboard_layout?: string;
  notifications?: {
    email?: boolean;
    push?: boolean;
    sms?: boolean;
  };
}

/**
 * Service pour la gestion des utilisateurs
 * Adapte automatiquement l'URL en fonction du rôle de l'utilisateur
 */
export class UserService {
  /**
   * Obtenir l'URL de base en fonction du contexte
   * Pour les super admins: /super-admin
   * Pour les admins tenant: /admin
   */
  private getBaseUrl(): string {
    const user = useAuthStore.getState().user;
    if (user?.role === 'super_admin') {
      return '/super-admin';
    }
    return '/admin';
  }

  /**
   * Récupérer la liste des utilisateurs avec filtres et pagination
   */
  async getUsers(filters: UserFilters = {}): Promise<PaginatedResponse<User>> {
    const baseUrl = this.getBaseUrl();
    const url = apiClient.buildUrl(`${baseUrl}/users`, filters);
    return apiClient.get<PaginatedResponse<User>>(url);
  }

  /**
   * Récupérer un utilisateur par son ID
   */
  async getUser(id: string): Promise<User> {
    const baseUrl = this.getBaseUrl();
    return apiClient.get<User>(`${baseUrl}/users/${id}`);
  }

  /**
   * Créer un nouvel utilisateur
   */
  async createUser(data: CreateUserData): Promise<User> {
    const baseUrl = this.getBaseUrl();
    return apiClient.post<User>(`${baseUrl}/users`, data);
  }

  /**
   * Mettre à jour un utilisateur
   */
  async updateUser(id: string, data: UpdateUserData): Promise<User> {
    const baseUrl = this.getBaseUrl();
    return apiClient.put<User>(`${baseUrl}/users/${id}`, data);
  }

  /**
   * Supprimer un utilisateur
   */
  async deleteUser(id: string): Promise<void> {
    const baseUrl = this.getBaseUrl();
    return apiClient.delete(`${baseUrl}/users/${id}`);
  }

  /**
   * Activer/désactiver un utilisateur
   */
  async toggleUserStatus(id: string, isActive: boolean): Promise<User> {
    const baseUrl = this.getBaseUrl();
    return apiClient.patch<User>(`${baseUrl}/users/${id}/status`, {
      is_active: isActive
    });
  }

  /**
   * Récupérer les statistiques des utilisateurs
   */
  async getUserStats(): Promise<UserStats> {
    const baseUrl = this.getBaseUrl();
    return apiClient.get<UserStats>(`${baseUrl}/users/stats`);
  }

  /**
   * Récupérer le profil de l'utilisateur connecté
   */
  async getCurrentProfile(): Promise<User> {
    return apiClient.get<User>('/admin/users/profile');
  }

  /**
   * Mettre à jour le profil utilisateur connecté
   */
  async updateProfile(data: UserProfileData): Promise<{ message: string; user: User }> {
    return apiClient.put<{ message: string; user: User }>('/admin/users/profile', data);
  }

  /**
   * Mettre à jour les préférences de l'utilisateur connecté
   */
  async updatePreferences(preferences: UserPreferences): Promise<{ message: string; preferences: any }> {
    return apiClient.put<{ message: string; preferences: any }>('/admin/users/profile/preferences', {
      preferences
    });
  }

  /**
   * Changer le mot de passe de l'utilisateur connecté
   */
  async changePassword(data: PasswordChangeData): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/admin/users/profile/change-password', data);
  }

  /**
   * Uploader un avatar pour l'utilisateur connecté
   */
  async uploadAvatar(avatar_url: string): Promise<{ message: string; avatar_url: string }> {
    return apiClient.post<{ message: string; avatar_url: string }>('/admin/users/profile/avatar', {
      avatar_url
    });
  }

  /**
   * Récupérer le profil de l'utilisateur connecté (alias pour compatibilité)
   * @deprecated Use getCurrentProfile instead
   */
  async getCurrentUser(): Promise<User> {
    return this.getCurrentProfile();
  }

  /**
   * Réinitialiser le mot de passe d'un utilisateur
   */
  async resetUserPassword(id: string, newPassword: string): Promise<void> {
    const baseUrl = this.getBaseUrl();
    return apiClient.patch(`${baseUrl}/users/${id}/reset-password`, {
      new_password: newPassword
    });
  }

  /**
   * Envoyer un email de vérification
   */
  async sendVerificationEmail(userId: string): Promise<void> {
    const baseUrl = this.getBaseUrl();
    return apiClient.post(`${baseUrl}/users/${userId}/send-verification`);
  }

  /**
   * Récupérer l'historique des connexions d'un utilisateur
   */
  async getUserLoginHistory(id: string): Promise<any[]> {
    const baseUrl = this.getBaseUrl();
    return apiClient.get<any[]>(`${baseUrl}/users/${id}/login-history`);
  }
}

// Instance singleton du service
export const userService = new UserService();